import { saveEquation } from '@common/prosemirror/commands/equation.command';
import { isLocalEquationInsert } from '@common/prosemirror/plugins/equation-insert.plugin';
import { ChangeType } from '@common/prosemirror/tracked-changes/change-type.enum';
import { attrsHaveChange } from '@common/prosemirror/tracked-changes/compare';
import { EditorEvent } from '@portal-core/project-files/events/editor-event.event';
import { NodeViewBase } from '@portal-core/text-editor/node-views/node-view.base';
import { GetPosForNodeView } from '@portal-core/text-editor/node-views/types/nodeview-get-pos.type';
import { extractEquationLatex, isEditableEquationNode } from '@portal-core/ui/equation/util/equation-node';
import { buildSavedEquationNode, renderEquationAsync, wrappedMathRegex } from '@portal-core/ui/equation/util/mathjax';
import { BidiBridge } from '@portal-core/util/bidi-bridge';
import { MathfieldElement } from 'mathlive';
import { ProseMirrorNode } from 'prosemirror-model';
import { NodeSelection, TextSelection } from 'prosemirror-state';
import { Decoration, EditorView, NodeView } from 'prosemirror-view';

export class EquationNodeView extends NodeViewBase implements NodeView {
  /**
   * Toggled on the document root (<html>) while an equation is edited inside a CDK overlay (a
   * dialog such as the Snippet or AI Assist editor) to lift MathLive's shared virtual keyboard
   * above the overlay layer — see the matching rule in _editor-flare-base.scss (Bug #191070).
   */
  private static readonly keyboardAboveOverlayClass = 'mc-equation-keyboard-above-overlay';

  private isEditing = false;
  /** Raw LaTeX without $ delimiters, kept in sync with the document node. */
  private currentLatex = '';
  /**
   * Created lazily on first enterEditMode() so MathLive's connectedCallback runs while
   * the element is visible. Kept in the DOM permanently so connectedCallback only runs once.
   */
  private mathField: MathfieldElement | null = null;
  private renderedDisplay: HTMLElement;
  /**
   * A scroll container sitting between this.dom (the MadCap:equation / math / mml:math element) and
   * the equation content — it wraps both renderedDisplay and, once created, the MathLive field.
   * Horizontal overflow (a wide rendered equation, a tracked-removal that can't wrap, or a MathLive
   * field wider than the editor) scrolls HERE, so this.dom can stay overflow: visible and not clip
   * the collab user-name tooltip drawn above it as an ::after (see _editor-flare-base.scss).
   */
  private scrollWrapper: HTMLElement;
  /**
   * Bumped on every syncNode() render attempt so a renderEquationAsync() call that resolves after
   * a later syncNode() has already re-rendered (e.g. collab delivered a newer value, or the
   * equation was edited again) can tell it's stale and skip writing its now-outdated result into
   * renderedDisplay.
   */
  private renderRequestId = 0;
  /**
   * Prevents collab-delivered temporary empty nodes from re-triggering edit mode. Also set when
   * this view's first edit session ends (resetEditModeUi()) so leaving the equation empty doesn't
   * keep re-scheduling auto-enter via isLocalEquationInsert(), which never expires on its own.
   */
  private hasRendered = false;
  /**
   * Set while a VK button is held or the pointer is down on this.dom outside of mf (e.g.
   * dragging the equation's own horizontal scrollbar) — both cases blur mf as a side effect;
   * this suppresses the blur handler from treating that as a click-away and exiting edit mode.
   * Tracks which case is active (rather than a plain boolean) because each clears on a different
   * event: 'vk' clears from the vk.executeCommand monkeypatch once the command completes,
   * 'scrollbar' clears on the next document pointerup. A shared boolean cleared by either path
   * would let the generic pointerup clear an in-flight VK press early.
   */
  private suppressBlurExitFor: 'vk' | 'scrollbar' | null = null;
  /** Atom-index drag anchor, set at pointerdown. -1 when no drag is active. */
  private dragAnchor = -1;
  /** Saved while editing to bypass the broken sendMessage/connectedMathfieldWindow channel. */
  private vkExecuteCommandOriginal: ((command: unknown) => boolean) | null = null;
  /**
   * PBI #186777: existing MathML equations are read-only (see the block in enterEditMode()) —
   * MathLive can only ever open a LaTeX-content equation for editing. This still comes into play
   * when a user pastes raw MathML into that LaTeX field mid-edit: MathLive's LaTeX
   * parser/serializer isn't a lossless round-trip for MathML (e.g. it can reflow whitespace around
   * a `:` in an `mml:` namespace prefix, treating it as the `\colon` relation macro), so
   * rawMathMlContent holds the pasted bytes verbatim, and exitEditMode() restores them exactly if
   * hasEditedMathField stays false — i.e. the user pasted and then left without any further edit —
   * instead of trusting mf.getValue()'s possibly-mangled output. A later paste overwrites this
   * baseline outright (mfPasteHandler always reassigns it) — there's no need to merge with what
   * was pasted before, since the newest paste is what the user now intends to save.
   */
  private rawMathMlContent: string | null = null;
  /** Set true by mfInputHandler on MathLive's 'input' event — any real edit to the field. */
  private hasEditedMathField = false;
  /**
   * The 'paste' and 'input' math-field handlers are kept as fields (unlike the other listeners,
   * which are locals whose removers are pushed to listenerCleanupFns) because the unit tests invoke
   * them directly to simulate MathLive's 'paste'/'input' events — there is no mounted field firing
   * real ones in those tests. Their removers are still pushed to listenerCleanupFns like the rest.
   */
  private mfPasteHandler: ((e: ClipboardEvent) => void) | null = null;
  private mfInputHandler: (() => void) | null = null;
  /**
   * Removers for every event listener this view registers — the always-on dom listeners added in
   * the constructor, plus all the edit-mode listeners (document / window / math field / shadow root
   * / virtual keyboard) added in setupMathFieldListeners(). Each addEventListener pushes its own
   * remover here, and destroy() runs them in one loop — mirroring NodeViewBase's arrowKeyCleanupFns.
   * Deliberately NOT routed through an AbortSignal: zone.js shares one native listener per
   * (target, event) and mishandles { signal } cleanup (see the vkVariantPanel note in
   * setupMathFieldListeners()), so plain removeEventListener is the safe teardown here.
   */
  private listenerCleanupFns: (() => void)[] = [];

  constructor(
    node: ProseMirrorNode,
    editorView: EditorView,
    getPos: GetPosForNodeView,
    decorations: Decoration[],
    nodeViewBridge: BidiBridge<EditorEvent, EditorEvent>
  ) {
    super(node, editorView, getPos, decorations, nodeViewBridge);

    // Create the DOM elements. renderedDisplay (and later the MathLive field) live inside a scroll
    // wrapper rather than directly under this.dom, so wide content scrolls on the wrapper and this.dom
    // itself can keep overflow: visible for the collab tooltip — see scrollWrapper's doc comment.
    this.scrollWrapper = document.createElement('mc-lwe-equation-scroll');
    this.renderedDisplay = document.createElement('mc-lwe-equation-content');
    this.scrollWrapper.appendChild(this.renderedDisplay);
    this.dom.appendChild(this.scrollWrapper);

    this.dom.setAttribute('contenteditable', 'false');

    // Listen for events to open the equation editor
    const enterEditModeHandler = () => this.enterEditMode();
    this.dom.addEventListener('dblclick', enterEditModeHandler);
    this.dom.addEventListener('edit-equation', enterEditModeHandler);
    this.listenerCleanupFns.push(() => {
      this.dom.removeEventListener('dblclick', enterEditModeHandler);
      this.dom.removeEventListener('edit-equation', enterEditModeHandler);
    });

    // Sync the node
    this.syncNode(node);
  }

  stopEvent(event: Event): boolean {
    return this.isEditing;
  }

  destroy() {
    if (this.isEditing) {
      // Discard, not save: a fast file-switch/reload tears this node view down before
      // exitEditMode(true)'s own commit gets a chance to run. Known, accepted gap — an
      // in-progress edit's tracked-change marker/MathML-restore can be lost on a fast teardown —
      // tracked as future work rather than patched here.
      this.exitEditMode(false);
    }

    // Remove every listener this view registered. Each addEventListener (in the constructor and in
    // setupMathFieldListeners) pushed its own remover onto listenerCleanupFns, so this single loop
    // is the whole teardown. Empty beyond the constructor's dom listeners if edit mode never began.
    for (const cleanup of this.listenerCleanupFns) {
      cleanup();
    }

    this.listenerCleanupFns = [];

    this.restoreVkExecuteCommand();
    super.destroy();
  }

  syncNode(node: ProseMirrorNode) {
    const incomingLatex = extractEquationLatex(node);

    if (this.isEditing) {
      // Never interrupt a user-initiated edit. Abandon only if real collab content arrives
      // for an auto-opened empty node (hasRendered=false).
      if (this.hasRendered || !incomingLatex) {
        return;
      }

      // Shares exitEditMode()'s teardown (see resetEditModeUi()) rather than hand-copying
      // it here, so this stays in sync if that list of "what does exiting mean" ever changes.
      this.resetEditModeUi();
    }

    this.currentLatex = incomingLatex;

    // Auto-enter edit mode only for a genuinely new empty equation (never rendered) that THIS
    // client just inserted — not one collab just delivered from another user's insert, which
    // would otherwise yank this client's focus/selection onto a node nobody here asked to edit
    // (see equationInsertPlugin's doc comment for the full story).
    // Collab can also temporarily deliver an empty node for existing equations — ignore those.
    if (!this.currentLatex) {
      // Clear any previous render first: ProseMirror/collab can reuse this view for a
      // different, brand-new empty node, and a stale render would look like leftover content.
      this.renderedDisplay.replaceChildren();
      this.dom.setAttribute('mc-lwe-equation-text', 'Empty equation');

      const pos = this.getPos();
      if (!this.hasRendered && pos != null && isLocalEquationInsert(this.editorView.state, pos)) {
        requestAnimationFrame(() => {
          if (!this.isDestroyed && !this.hasRendered) {
            this.enterEditMode();
          }
        });
      }

      return;
    }

    this.dom.removeAttribute('mc-lwe-equation-text');

    const isTrackedRemoval = attrsHaveChange(node.attrs, ChangeType.Remove);
    const equationToRender = isTrackedRemoval
      ? this.withHorizontalStrike(this.currentLatex)
      : this.currentLatex;

    const requestId = ++this.renderRequestId;

    // Async so a not-yet-cached font glyph just resolves later instead of throwing. No
    // placeholder — renderedDisplay stays as-is until the Promise settles, matching this view's
    // pre-MathLive-editor behavior. requestId/isDestroyed guard against a stale render landing late.
    renderEquationAsync(equationToRender)
      .then(rendered => {
        if (this.isDestroyed || requestId !== this.renderRequestId) {
          return;
        }

        this.renderedDisplay.replaceChildren(rendered);
        this.emitEvent('node-view-size-changed', { nodeName: node.type.name });
      })
      .catch(error => {
        if (this.isDestroyed || requestId !== this.renderRequestId) {
          return;
        }

        console.error('[EQ] renderEquationAsync rejected:', error);
        this.renderedDisplay.replaceChildren(document.createTextNode(this.currentLatex));
        this.emitEvent('node-view-size-changed', { nodeName: node.type.name });
      });

    this.hasRendered = true;
  }

  /**
   * Pushes mf's current undo/redo/selection state to the virtual keyboard toolbar.
   * NOTE: update()/MathfieldProxy are typed but marked @internal in MathLive's own .d.ts —
   * not a documented public API. If a MathLive upgrade breaks this, the failure mode is a
   * stale/empty toolbar, not broken typing.
   */
  private updateVirtualKeyboardToolbar(mf: MathfieldElement) {
    (window as any).mathVirtualKeyboard?.update?.({
      canUndo: mf.canUndo(),
      canRedo: mf.canRedo(),
      selectionIsCollapsed: mf.selectionIsCollapsed,
      mode: mf.mode,
      style: {}
    });
  }

  /**
   * MathLive's normalizeLayout() special-cases the "alphabetic" (Roman Letters) tab: it returns
   * from alphabeticLayout() early, before the shared logic further down that computes
   * displayEditToolbar for every other tab (numeric, symbols, greek). That leaves
   * displayEditToolbar undefined for Roman Letters alone, so makeLayout() never renders its
   * .ML__edit-toolbar div there — the Undo/Redo/Paste from Clipboard buttons are simply missing
   * from that tab's markup, not just hidden. Fixed upstream in MathLive 0.105.1, but bumping to
   * it pulls in a @cortex-js/compute-engine version whose .d.ts isn't compilable under this
   * repo's TypeScript 4.9.4 — so patch the div in ourselves instead of upgrading. Once the div
   * exists, MathLive's own update()/updateToolbar() keeps its content and click handling in sync
   * exactly like every other tab's toolbar, since both only look the div up by class name.
   * Idempotent (no-ops once the div exists) — cheap to call on every focus.
   */
  private ensureAlphabeticEditToolbar() {
    const vk = (window as any).mathVirtualKeyboard;
    const layouts = vk?.normalizedLayouts as any[] | undefined;
    const alphabeticLayout = layouts?.find(l => l?.tooltip === 'keyboard.tooltip.alphabetic');
    const layerId = alphabeticLayout?.layers?.[0]?.id;
    if (!layerId) {
      return;
    }

    const layerEl = document.getElementById(layerId);
    const toolbarEl = layerEl?.querySelector('.MLK__toolbar');
    if (!toolbarEl || toolbarEl.querySelector('.ML__edit-toolbar')) {
      return;
    }

    const editToolbar = document.createElement('div');
    editToolbar.className = 'ML__edit-toolbar right';

    // Mirrors MathLive's own click handler in makeKeyboardElement() — that one only gets wired
    // up for the toolbars present in the DOM when the panel was originally built, which never
    // included this one.
    editToolbar.addEventListener('click', (ev: MouseEvent) => {
      let target = ev.target as Element | null;
      let command = '';
      while (target && !command) {
        command = target.getAttribute('data-command') ?? '';
        target = target.parentElement;
      }
      if (command) {
        vk.executeCommand(JSON.parse(command));
      }
    });

    toolbarEl.appendChild(editToolbar);
  }

  /**
   * Rebuilds the MathLive command array for a keycap, mirroring MathLive's own internal
   * `executeKeycapCommand`: precedence is command → insert → key → latex → label. Used by the
   * stuck-press fallback (vkFallback) to replay a keycap whose native per-press listener
   * never fired. The `keycap` here is either a keycap definition or its `.shift` variant object.
   */
  private buildKeycapCommand(keycap: any): unknown {
    if (keycap.command) {
      return keycap.command;
    }

    if (keycap.insert) {
      return ['insert', keycap.insert, { focus: true, feedback: true, scrollIntoView: true, mode: 'math', format: 'latex' }];
    }

    if (keycap.key) {
      return ['typedText', keycap.key, { focus: true, feedback: true, simulateKeystroke: true }];
    }

    if (keycap.latex) {
      return ['insert', keycap.latex, { focus: true, feedback: true, scrollIntoView: true, mode: 'math', format: 'latex' }];
    }

    return ['typedText', keycap.label, { focus: true, feedback: true, simulateKeystroke: true }];
  }

  /** Registers all math-field event listeners. Called once on first enterEditMode(). */
  private setupMathFieldListeners(mf: MathfieldElement) {
    // NOTE: do not set (window as any).mathVirtualKeyboard.editToolbar here — 'default' is
    // already MathLive's built-in value, and the setter's rebuild() side effect tears down
    // the shared keyboard panel. Since this runs once per equation (not once globally), doing
    // it per-equation can rebuild the panel while it's still connected to another equation's
    // field, breaking that connection.
    const internalMf = (mf as any)._mathfield;
    // Suppress disconnectFromVirtualKeyboard while editing — shadow-DOM keyboard sink blur
    // triggers onBlur() which clears connectedMathfieldWindow, silently dropping VK commands.
    if (internalMf?.disconnectFromVirtualKeyboard) {
      const origDisconnect = internalMf.disconnectFromVirtualKeyboard.bind(internalMf);

      internalMf.disconnectFromVirtualKeyboard = () => {
        if (this.isEditing) {
          return;
        }

        origDisconnect();
      };
    }

    // Patch setValue to skip selectAllChildren(keyboardSink). MathLive calls it on every
    // selection change, leaving a non-collapsed browser selection that Chrome uses to trigger
    // drag-text mode on the next pointerdown (suppressing all pointermove events). Copy still
    // works — onCopy reads model.selection and falls back to a synthetic ClipboardEvent.
    const keyboardDelegate = internalMf?.keyboardDelegate;
    if (keyboardDelegate?.setValue) {
      const origSetValue = keyboardDelegate.setValue.bind(keyboardDelegate);

      keyboardDelegate.setValue = (value: string) => {
        const ks = (mf as any).shadowRoot?.querySelector('.ML__keyboard-sink') as HTMLElement | null;
        if (!ks) {
          origSetValue(value);
          return;
        }

        if (ks.textContent !== value) {
          ks.textContent = value;
        }

        // selectAllChildren intentionally omitted — see comment above
      };
    }

    // Capture pointerdown: preventDefault for VK (keeps mf focus), exit for outside clicks.
    const vkPointerDownHandler = (e: PointerEvent) => {
      if (!this.isEditing) {
        return;
      }

      const target = e.target as Element;

      if (target.closest('.ML__keyboard')) {
        // Do NOT use setPointerCapture — it causes a deferred pointerleave that leaves
        // 'is-pressed' on the button, breaking every other VK press.
        e.preventDefault();

        this.suppressBlurExitFor = 'vk';
        return;
      }


      const toggleButtons = ['virtual-keyboard-toggle', 'menu-toggle'];
      // mf's shadow root uses delegatesFocus, so a pointerdown on its own (non-focusable)
      // toggle buttons re-triggers native focus delegation and silently steals focus from
      // MathLive's input. preventDefault() blocks that without blocking the button's
      // own click handler. e.target is retargeted to mf, so use composedPath() to see inside.
      if (e.composedPath().some(el => el instanceof Element && toggleButtons.includes(el.getAttribute('part') ?? ''))) {
        e.preventDefault();
        return;
      }

      // this.dom (not just mf) — the equation's horizontal-scroll wrapper (scrollWrapper) sits
      // between this.dom and mf, so dragging its scrollbar targets an element inside this.dom and
      // would otherwise look like an outside click and prematurely exit edit mode. Dragging the
      // scrollbar also blurs mf (a separate event path) — flag it so the blur handler refocuses
      // instead of exiting.
      if (this.dom === target || this.dom.contains(target)) {
        this.suppressBlurExitFor = 'scrollbar';
        return;
      }

      // The browser's own subsequent 'click' can land on the wrong element here — confirmed via
      // instrumentation that with an equation open, a pointerdown on e.g. a file-tree item's
      // label correctly targets that element, but the 'click' the browser dispatches right after
      // lands on an ancestor (a CDK virtual-scroll wrapper div) instead, so the tree item's own
      // click handler never runs and nothing happens until a second click. This reproduces even
      // with exitEditMode fully deferred out of the way, so it isn't a timing race with our own
      // code — something about this specific pointerdown/click sequence confuses the browser's
      // hit-testing for click. `target` here (captured at pointerdown, before anything else runs)
      // is reliably correct, so replay a real click on it directly rather than trust the browser's.
      (target as HTMLElement).click?.();

      // Commit any uncommitted MathLive raw LaTeX-entry buffer (e.g. after right-click > Mode >
      // LaTeX), which otherwise only commits on Enter/Escape/Tab and would save as empty here.
      mf.executeCommand('complete');

      // Don't refocus the editor here — this is a click on something else entirely (e.g. a
      // different topic in the file tree); let that click's own target keep the focus.
      this.exitEditMode(true, false);
    };

    document.addEventListener('pointerdown', vkPointerDownHandler, { capture: true });
    this.listenerCleanupFns.push(() => document.removeEventListener('pointerdown', vkPointerDownHandler, { capture: true }));

    // Clears suppressBlurExitFor after a scrollbar drag ends. Only clears the 'scrollbar' case —
    // an in-flight VK press clears itself via the vk.executeCommand monkeypatch (in enterEditMode)
    // once the command completes, not on every pointerup a multi-step VK gesture generates.
    const pointerUpClearHandler = () => {
      if (this.suppressBlurExitFor === 'scrollbar') {
        this.suppressBlurExitFor = null;
      }
    };

    document.addEventListener('pointerup', pointerUpClearHandler, { capture: true });
    this.listenerCleanupFns.push(() => document.removeEventListener('pointerup', pointerUpClearHandler, { capture: true }));

    // zone.js shares one real native listener per (target, event) and doesn't know about
    // AbortSignal, so if MathLive's variants popup (e.g. holding '4') is ever the first to
    // register a window pointerup/pointercancel listener, its own `{ signal }` cleanup silently
    // kills that shared listener for good — breaking dismissal for every popup after the first.
    // These no-op, signal-free listeners just claim that first-registration slot so it can't
    // happen.
    const vkVariantPanelPointerUpHandler = () => { };
    const vkVariantPanelPointerCancelHandler = () => { };

    window.addEventListener('pointerup', vkVariantPanelPointerUpHandler);
    window.addEventListener('pointercancel', vkVariantPanelPointerCancelHandler);
    this.listenerCleanupFns.push(() => window.removeEventListener('pointerup', vkVariantPanelPointerUpHandler));
    this.listenerCleanupFns.push(() => window.removeEventListener('pointercancel', vkVariantPanelPointerCancelHandler));

    // Bubble-phase pointerdown on mf: record atom-index anchor after MathLive sets model.position.
    const mfPointerDownBubbleHandler = (e: PointerEvent) => {
      if (!this.isEditing || e.button !== 0) {
        return;
      }

      this.dragAnchor = (mf as any)._mathfield?.model?.position ?? -1;
    };

    mf.addEventListener('pointerdown', mfPointerDownBubbleHandler);
    this.listenerCleanupFns.push(() => mf.removeEventListener('pointerdown', mfPointerDownBubbleHandler));

    // Capture a pasted MathML string before letting MathLive parse it as LaTeX (lossy — see
    // rawMathMlContent's doc comment). MathLive fires its own 'input' event for this same paste
    // (handled by mfInputHandler below), so hasEditedMathField gets set true by it — clear that
    // back to false afterward, since the paste itself isn't a user edit of the MathML content,
    // it's what established it. Any *further* input after this resets it true again correctly.
    const pasteHandler = (e: ClipboardEvent) => {
      if (!this.isEditing) {
        return;
      }

      const pasted = e.clipboardData?.getData('text/plain')?.trim();
      if (!pasted || !wrappedMathRegex.test(pasted)) {
        this.rawMathMlContent = null;
        return;
      }

      this.rawMathMlContent = pasted;
      // A microtask (not requestAnimationFrame) — this only needs to run after the current
      // event's synchronous handlers finish, not after a paint. rAF can be delayed indefinitely
      // if the tab is backgrounded right after pasting, and doesn't fire at all in some headless
      // test environments.
      Promise.resolve().then(() => {
        if (!this.isDestroyed && this.isEditing) {
          this.hasEditedMathField = false;
        }
      });
    };

    this.mfPasteHandler = pasteHandler;
    mf.addEventListener('paste', this.mfPasteHandler);
    this.listenerCleanupFns.push(() => mf.removeEventListener('paste', pasteHandler));

    // Any real MathLive edit (typing, VK input, etc.) fires 'input' — including the initial
    // paste above, whose effect mfPasteHandler's queued microtask undoes afterward. A second,
    // unrelated 'input' listener further down (near updateVirtualKeyboardToolbar) refreshes the
    // VK toolbar on the same event — kept separate since it serves a different concern.
    const inputHandler = () => {
      this.hasEditedMathField = true;
    };

    this.mfInputHandler = inputHandler;
    mf.addEventListener('input', this.mfInputHandler);
    this.listenerCleanupFns.push(() => mf.removeEventListener('input', inputHandler));

    // Drag fallback: extends selection when MathLive's onPointerMove aborts (hasFocus() false
    // after keyboard-sink blur) or was never started (onPointerDown guard skipped).
    const dragPointermoveHandler = (e: PointerEvent) => {
      if (!this.isEditing || this.dragAnchor < 0 || e.buttons !== 1) {
        return;
      }

      const focus = (mf as any).getOffsetFromPoint?.(e.clientX, e.clientY) ?? -1;
      if (focus < 0 || focus === this.dragAnchor) {
        return;
      }

      (mf as any).selection = focus > this.dragAnchor
        ? { ranges: [[this.dragAnchor, focus]], direction: 'forward' }
        : { ranges: [[focus, this.dragAnchor]], direction: 'backward' };
    };

    document.addEventListener('pointermove', dragPointermoveHandler, { capture: true });
    this.listenerCleanupFns.push(() => document.removeEventListener('pointermove', dragPointermoveHandler, { capture: true }));

    // Resets drag anchor and collapses any leftover keyboard-sink browser selection.
    const dragPointerupHandler = (_e: PointerEvent) => {
      this.dragAnchor = -1;
      if (!this.isEditing) {
        return;
      }

      setTimeout(() => {
        if (!this.isEditing) {
          return;
        }

        const ks = (mf as any).shadowRoot?.querySelector('.ML__keyboard-sink');
        if (!ks) {
          return;
        }

        const sel = window.getSelection();
        if (!sel || sel.isCollapsed) {
          return;
        }

        try {
          const range = sel.rangeCount > 0 ? sel.getRangeAt(0) : null;
          if (range && (ks === range.commonAncestorContainer || ks.contains(range.startContainer))) {
            sel.collapse(ks, 0);
          }
        } catch (_) { }
      }, 0);
    };

    document.addEventListener('pointerup', dragPointerupHandler, { capture: true });
    this.listenerCleanupFns.push(() => document.removeEventListener('pointerup', dragPointerupHandler, { capture: true }));

    // Fallback for a MathLive bug: if 'is-pressed' still set after pointerup, MathLive's
    // per-press listener never fired — replay the keycap command manually.
    const vkFallback = (e: PointerEvent) => {
      if (!this.isEditing) {
        return;
      }

      const t = e.target as Element;
      if (!t.closest?.('.ML__keyboard')) {
        return;
      }

      let node: Element | null = t;
      while (node) {
        const c = node.classList;
        if (c.contains('MLK__keycap') || c.contains('shift') || c.contains('action') || c.contains('fnbutton') || c.contains('bigfnbutton')) {
          break;
        }

        node = node.parentElement;
      }

      const btn = node as HTMLElement | null;
      if (!btn?.id || !btn.classList.contains('is-pressed')) {
        return;
      }

      const btnId = btn.id;

      setTimeout(() => {
        if (!this.isEditing || this.isDestroyed) {
          return;
        }

        const b = document.getElementById(btnId);
        if (!b?.classList.contains('is-pressed')) {
          return;
        }
        b.classList.remove('is-pressed');

        const vk = (window as any).mathVirtualKeyboard;
        if (!vk) {
          return;
        }
        const keycap = vk.getKeycap?.(btnId);
        if (!keycap) {
          return;
        }
        // Shift keys use toggle logic, not execute — skip them.
        if (keycap.class && /(?:^|\s)shift(?:\s|$)/.test(keycap.class)) {
          return;
        }

        // When the keyboard is shifted — Shift pressed once, or twice for caps-lock — MathLive
        // inserts the keycap's `.shift` variant instead of its primary value. The native pointerup
        // handler we're standing in for does this; mirror it here, or a shifted keycap replays its
        // primary character (e.g. 'x' instead of 'y'). The `.shift` variant is either a plain latex
        // string or a full keycap definition. bug #191044
        let command: unknown;
        if (vk.isShifted && keycap.shift) {
          command = typeof keycap.shift === 'string'
            ? ['insert', keycap.shift, { focus: true, feedback: true, scrollIntoView: true, mode: 'math', format: 'latex' }]
            : this.buildKeycapCommand(keycap.shift);
        } else {
          command = this.buildKeycapCommand(keycap);
        }

        vk.executeCommand(command);

        // Mirror MathLive: a one-shot Shift (shiftPressCount === 1) is consumed by a single
        // keypress and resets to unshifted; caps-lock (=== 2) persists. shiftPressCount exists only
        // on the real VirtualKeyboard, not the proxy, so the `=== 1` guard keeps this a no-op there.
        if (vk.shiftPressCount === 1 && !e.shiftKey) {
          vk.shiftPressCount = 0;
        }
      }, 0);
    };

    document.addEventListener('pointerup', vkFallback, { capture: true });
    this.listenerCleanupFns.push(() => document.removeEventListener('pointerup', vkFallback, { capture: true }));

    const vkGeometryChangeHandler = () => {
      if (this.isEditing) {
        this.dom.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    };

    (window as any).mathVirtualKeyboard?.addEventListener?.('geometrychange', vkGeometryChangeHandler);
    this.listenerCleanupFns.push(() => (window as any).mathVirtualKeyboard?.removeEventListener?.('geometrychange', vkGeometryChangeHandler));

    // Catch dragstart at shadow root and host — pointerdown preventDefault covers mousedown
    // but not all browser drag paths.
    const shadowRoot: ShadowRoot | null = mf.shadowRoot;
    const shadowDragStartHandler = (e: Event) => e.preventDefault();
    shadowRoot?.addEventListener('dragstart', shadowDragStartHandler, { capture: true });
    this.listenerCleanupFns.push(() => shadowRoot?.removeEventListener('dragstart', shadowDragStartHandler, { capture: true }));

    const dragStartHandler = (e: Event) => e.preventDefault();
    mf.addEventListener('dragstart', dragStartHandler);
    this.listenerCleanupFns.push(() => mf.removeEventListener('dragstart', dragStartHandler));

    const focusInHandler = () => {
      (window as any).mathVirtualKeyboard?.show();

      // Bug: Undo/Redo/Paste from Clipboard are missing from the Roman Letters tab — see
      // ensureAlphabeticEditToolbar()'s doc comment.
      this.ensureAlphabeticEditToolbar();

      // MathLive only refreshes the toolbar's undo/redo/copy/paste buttons on the next
      // keystroke (mf's internal connectToVirtualKeyboard() runs before the keyboard is
      // visible, so its own post-connect update is skipped) — force one here so the
      // toolbar reflects the field's state as soon as the keyboard opens.
      this.updateVirtualKeyboardToolbar(mf);
    };
    mf.addEventListener('focusin', focusInHandler);
    this.listenerCleanupFns.push(() => mf.removeEventListener('focusin', focusInHandler));

    // The toolbar's undo/redo disabled state is baked into its HTML at render time, not
    // live-bound — MathLive's own per-edit update calls to the keyboard don't reliably reach
    // it in this embedding (the same class of VK-connection quirks documented elsewhere in
    // this file), so the button stays visually stuck at whatever state it had on focus even
    // though it keeps working. Re-sync explicitly on every edit. A second, separate 'input'
    // listener above (mfInputHandler) tracks hasEditedMathField for MathML preservation — kept
    // apart since it's an unrelated concern, not because it couldn't share one listener.
    const toolbarInputHandler = () => this.updateVirtualKeyboardToolbar(mf);
    mf.addEventListener('input', toolbarInputHandler);
    this.listenerCleanupFns.push(() => mf.removeEventListener('input', toolbarInputHandler));

    // NOTE: closing the virtual keyboard is deliberately NOT done from a 'focusout' handler here —
    // it's handled by resetEditModeUi(), tied to exiting edit mode. An earlier 'focusout'-driven
    // hide() raced with enterEditMode()'s show() and flashed the keyboard shut on the first click /
    // after a window refocus, so it needed a second click to stay open (Bug #191007). Every genuine
    // exit (click-away, Enter/Escape, Tab/programmatic blur, move-out, destroy, collab abandon)
    // already routes through resetEditModeUi(), so nothing here needs to close it on focus loss.

    // Stop beforeinput from reaching ProseMirror — PM.preventDefault() aborts MathLive commands.
    const beforeInputHandler = (e: Event) => { e.stopPropagation(); };
    mf.addEventListener('beforeinput', beforeInputHandler);
    this.listenerCleanupFns.push(() => mf.removeEventListener('beforeinput', beforeInputHandler));

    // Stop keydown from reaching ProseMirror (arrows would move the PM cursor).
    const keydownHandler = (e: KeyboardEvent) => {
      e.stopPropagation();
      if (e.key === 'Enter' || e.key === 'Escape') {
        e.preventDefault();
        this.exitEditMode(e.key === 'Enter');
      }
    };
    mf.addEventListener('keydown', keydownHandler);
    this.listenerCleanupFns.push(() => mf.removeEventListener('keydown', keydownHandler));

    // move-out: exit on physical keyboard edge-nav; no-op for VK arrows (suppressBlurExitFor is
    // still 'vk' here because move-out fires synchronously inside executeCommand()).
    const moveOutHandler = (e: Event) => {
      e.preventDefault();

      if (this.suppressBlurExitFor === 'vk') {
        return;
      }

      this.exitEditMode(true);
    };
    mf.addEventListener('move-out', moveOutHandler);
    this.listenerCleanupFns.push(() => mf.removeEventListener('move-out', moveOutHandler));

    // Exit on Tab/programmatic focus loss; refocus during an in-flight VK press or scrollbar drag.
    const blurHandler = () => {
      if (this.isEditing && this.suppressBlurExitFor) {
        mf.focus();
      }

      setTimeout(() => {
        if (!this.isEditing || this.isDestroyed) {
          return;
        }

        if (this.suppressBlurExitFor) {
          // Press/drag still in flight — the vk.executeCommand monkeypatch (VK) or
          // pointerUpClearHandler (scrollbar) will clear it.
          mf.focus();
          return;
        }

        const shadowActive = (mf as any).shadowRoot?.activeElement;
        if (document.activeElement === mf || shadowActive) {
          return;
        }

        if ((document.activeElement as Element)?.closest?.('.ML__keyboard')) {
          return;
        }

        this.exitEditMode(true);
      }, 0);
    };
    mf.addEventListener('blur', blurHandler);
    this.listenerCleanupFns.push(() => mf.removeEventListener('blur', blurHandler));
  }

  private enterEditMode() {
    if (this.isEditing || this.isDestroyed) {
      return;
    }

    // See isEditableEquationNode()'s doc comment (PBI #186777): MathML equations are read-only.
    if (!isEditableEquationNode(this.node)) {
      return;
    }

    // Bug #191069: dblclick/edit-equation bypassed the editor's own readonly gating (unlike
    // ProseMirror's own commands, which the editable prop already blocks) — a user without
    // Create/Edit Files permission could still open and edit an equation's MathLive field.
    if (!this.editorView.props?.editable(this.editorView.state)) {
      return;
    }

    this.isEditing = true;

    // Only lift the keyboard when this equation lives inside an overlay (dialog); page-level
    // editing keeps the keyboard's default stacking so snackbars still render over it.
    if (this.dom.closest('.cdk-overlay-container')) {
      document.documentElement.classList.add(EquationNodeView.keyboardAboveOverlayClass);
    }

    // Remove contenteditable="false" before creating or showing the math field — it cascades
    // user-select:none which MathLive detects to disable pointer events, and stopEvent() keeps
    // ProseMirror from handling events while isEditing is true. Done before the node-selection
    // dispatch below so that, by the time that dispatch drives the equation toolbar popup's plugin
    // view, isEquationInEditMode() already reports true (it keys off this attribute being gone) and
    // keeps the popup closed — dismissing that popup was the real job of the old collapse-to-a-caret.
    this.dom.removeAttribute('contenteditable');

    // Select the equation node itself rather than collapsing to a bare cursor after it. The editor
    // selection is exactly what's broadcast to collaborators (collab-session's createCollabUserUpdate
    // sends state.selection.toJSON()), and only a 'node'-type selection makes the collab-users plugin
    // draw a highlight around the whole equation for the other users — a collapsed text selection
    // renders as a lone caret beside it instead ("collaborators only see a vertical cursor to the
    // right of an equation being edited"). exitEditMode() collapses this back to a caret just after
    // the node on a normal exit, so the local post-edit cursor position is unchanged.
    const editPos = this.getPos();

    if (editPos != null) {
      this.editorView.dispatch(
        this.editorView.state.tr.setSelection(NodeSelection.create(this.editorView.state.doc, editPos))
      );
    }

    if (!this.mathField) {
      // Static — applies to all mathfields. MathLive plays keypress sounds by default
      // (soundsDirectory defaults to './sounds'); suppress them.
      MathfieldElement.soundsDirectory = null;

      // Static. Without this, a window blur/refocus after we've exited edit mode makes MathLive
      // silently refocus() the dismissed (but still-mounted) mathfield, desyncing its internal
      // 'blurred' flag — the next enterEditMode()'s focus() then no-ops, breaking keyboard input
      // and mouse-drag selection. We manage focus ourselves, so disable MathLive's auto-restore.
      MathfieldElement.restoreFocusWhenDocumentFocused = false;

      const mf = new MathfieldElement();
      // Safety net: MathLive's connectedCallback disables pointer events if it sees
      // user-select:none on the host at mount time.
      mf.style.setProperty('user-select', 'text', 'important');
      mf.mathVirtualKeyboardPolicy = 'manual';
      (mf as any).popoverPolicy = 'off';

      // Into the scroll wrapper, alongside renderedDisplay — so a MathLive field wider than the
      // editor scrolls within the wrapper instead of overflowing (or clipping the collab tooltip on
      // this.dom). See scrollWrapper's doc comment.
      this.scrollWrapper.appendChild(mf);

      // Fix for bug #191049 - CC and QQ shortcuts erroneously insert \mathbb{\C} and \mathbb{\Q} instead of \mathbb{C} and \mathbb{Q}
      mf.inlineShortcuts = {
        ...mf.inlineShortcuts,
        CC: '\\mathbb{C}',
        QQ: '\\mathbb{Q}'
      };

      // MathLive's own default Escape keybinding switches between math/text and LaTeX edit mode
      // instead of reaching the 'keydown' listener below that exits edit mode entirely — remove
      // it so Escape always exits, matching this component's own Escape/Enter handling.
      mf.keybindings = mf.keybindings.filter(keybinding => !(keybinding.key === '[Escape]' && Array.isArray(keybinding.command) && keybinding.command[0] === 'switchMode'));

      this.mathField = mf;
      this.setupMathFieldListeners(mf);
    }

    // Route VK commands directly to mf.executeCommand — sendMessage/connectedMathfieldWindow
    // breaks after any blur/refocus cycle.
    const vk = (window as any).mathVirtualKeyboard;
    if (vk?.executeCommand) {
      this.vkExecuteCommandOriginal = vk.executeCommand.bind(vk);
      vk.executeCommand = (command: unknown) => {
        if (this.isEditing && this.mathField) {
          // VK Enter arrives as a raw string before parseCommand() normalizes it.
          const isCommit = command === 'commit' ||
            command === 'performWithFeedback(commit)' ||
            (Array.isArray(command) && command.some(c => c === 'commit' || c === 'performWithFeedback(commit)'));
          if (isCommit) {
            this.suppressBlurExitFor = null;
            this.exitEditMode(true);
            return true;
          }
          // Do NOT call mf.focus() here — performWithFeedback already calls it internally,
          // and a second call triggers scrollIntoView() causing a synchronous pointerleave.
          const result = (this.mathField as any).executeCommand(command);
          this.suppressBlurExitFor = null;
          return result;
        }

        return this.vkExecuteCommandOriginal!(command);
      };
    }

    // Show before setting value — MathLive may skip internal model updates on display:none
    // elements, causing the field to render empty when shown if value is set while hidden.
    this.renderedDisplay.style.display = 'none';
    this.mathField.style.display = '';
    this.mathField.value = this.currentLatex;

    // currentLatex can never be MathML here — the block above already returned for that case —
    // so any MathML baseline for this session can only come from a paste during editing (captured
    // by mfPasteHandler below). Reset any leftover baseline from a previous edit session.
    this.rawMathMlContent = null;
    this.hasEditedMathField = false;

    // Open the virtual keyboard as part of entering edit mode instead of relying on the mathfield's
    // 'focusin' event (fired by the rAF mf.focus() below) to open it. MathfieldElement.focus() is a
    // no-op whenever MathLive already believes the field is focused: its focus() short-circuits on an
    // internal `blurred` flag, not the real document.activeElement (mathlive's hasFocus() returns
    // `!blurred`). That flag goes stale after the browser window/tab loses and regains focus — the
    // rAF focus() then silently does nothing, 'focusin' never fires, and the keyboard never opens
    // (Bug #191007: the built-in keyboard "usually needs two clicks", and never opens on the first
    // click after a window refocus). Showing it here makes opening deterministic and independent of
    // focus; show() is idempotent, so the 'focusin' handler re-asserting it is harmless. The matching
    // hide() lives in resetEditModeUi() so closing is tied to *exiting* edit mode, not to transient
    // focus loss (see the note where the old 'focusout'-driven hide used to be).
    vk?.show?.();
    // Mirror the 'focusin' handler's post-show sequence here too: since this path exists precisely
    // because 'focusin' may never fire, anything the keyboard needs on open must run here as well.
    // ensureAlphabeticEditToolbar() patches the Roman Letters tab's missing Undo/Redo/Paste buttons
    // (Bug #191083) and updateVirtualKeyboardToolbar() syncs the toolbar's enabled state; both are
    // idempotent, so the 'focusin' handler re-running them when focus does land is harmless.
    this.ensureAlphabeticEditToolbar();
    this.updateVirtualKeyboardToolbar(this.mathField!);

    requestAnimationFrame(() => {
      if (!this.isDestroyed && this.isEditing) {
        // Re-assert in case an async layout pass reset user-select after connectedCallback.
        this.mathField!.style.setProperty('user-select', 'text', 'important');
        this.mathField!.focus();
      }
    });
  }

  private restoreVkExecuteCommand() {
    if (!this.vkExecuteCommandOriginal) {
      return;
    }

    const vk = (window as any).mathVirtualKeyboard;
    if (vk) {
      vk.executeCommand = this.vkExecuteCommandOriginal;
    }

    this.vkExecuteCommandOriginal = null;
  }

  /**
   * Resets the "not editing" UI/state shared by exitEditMode() and syncNode()'s collab-abandon
   * branch: clears the editing flag, restores the VK command routing, and swaps the math field
   * back out for the rendered display and this.dom's contenteditable="false"
   * (the latter is what isEquationInEditMode() checks — leaving it out reopens the exact bug this
   * shared method was extracted to prevent from recurring in a second hand-copy of this list).
   * Does not touch rawMathMlContent/hasEditedMathField or dispatch any transaction — those are
   * exitEditMode()'s own concern, since syncNode()'s abandon branch must not save/delete anything
   * (the document already changed out from under it via collab).
   */
  private resetEditModeUi() {
    this.isEditing = false;
    // See hasRendered's doc comment — this stops a still-empty equation from re-scheduling auto-enter.
    this.hasRendered = true;
    // Drop the keyboard elevation added in enterEditMode() (a no-op when it wasn't set, e.g.
    // page-level editing). This is the single teardown choke point, so it also covers destroy().
    document.documentElement.classList.remove(EquationNodeView.keyboardAboveOverlayClass);
    // Close the shared virtual keyboard here, at the single teardown choke point every exit path
    // funnels through (exitEditMode, syncNode's collab-abandon branch, destroy) — rather than from a
    // 'focusout' handler. Tying the hide to *leaving* edit mode instead of to the field losing focus
    // is what stops the keyboard flashing shut / needing a second click (Bug #191007): a transient
    // focus loss while still editing (window refocus, pressing MathLive's own keyboard toggle) no
    // longer tears it down, while every genuine exit still closes it. hide() is a no-op when the
    // keyboard isn't visible.
    (window as any).mathVirtualKeyboard?.hide?.();
    this.restoreVkExecuteCommand();
    this.mathField!.style.display = 'none';
    this.renderedDisplay.style.display = '';
    this.dom.setAttribute('contenteditable', 'false');
  }

  /**
   * Resolves the string to persist when exiting edit mode. MathLive's raw-MathML round-trip isn't
   * lossless — any genuine edit corrupts whitespace in its internal model regardless of getValue()
   * mode (repaired downstream, not here — see repairMathMlCorruption() in mathjax.ts). If MathML
   * was pasted into the field and nothing further was typed, bypass MathLive entirely and restore
   * the byte-exact snapshot from that paste (rawMathMlContent) instead — avoiding the corruption
   * beats repairing it. Otherwise use 'latex-expanded', which expands MathLive's own macros (e.g.
   * numeric-keypad e/i) to plain LaTeX our separate MathJax renderer can parse. If that comes back
   * empty on an untouched field, MathLive's custom element likely hasn't processed
   * enterEditMode()'s .value assignment yet — fall back to currentLatex rather than reading it as
   * a real empty save.
   */
  private resolveSavedLatex(mf: MathfieldElement): string {
    if (this.rawMathMlContent && !this.hasEditedMathField) {
      return this.rawMathMlContent;
    }

    const value = mf.getValue('latex-expanded')?.trim();
    if (value) {
      return value;
    }

    return this.hasEditedMathField ? '' : this.currentLatex;
  }

  /**
   * @param refocus Whether to return focus to the outer editor after exiting. Pass false when
   * exiting because the user clicked/is clicking a different element (e.g. another topic in the
   * file tree) — refocusing here during that same pointerdown steals focus from the click's own
   * target before the browser decides whether to fire its click event, which can silently cancel
   * that click (the user sees the equation deselect but nothing else happens until they click again).
   */
  private exitEditMode(save: boolean, refocus: boolean = true) {
    if (!this.isEditing) {
      return;
    }

    const mf = this.mathField!;
    const newLatex = this.resolveSavedLatex(mf);
    // Did the user touch the field this session
    const hasContentChange = this.hasEditedMathField || !!this.rawMathMlContent;

    this.rawMathMlContent = null;
    this.hasEditedMathField = false;

    this.resetEditModeUi();

    const pos = this.getPos();
    if (pos == null) {
      return;
    }

    // Bug #191062: opening an equation and clicking away without editing it still dispatched a
    // real tr.replaceWith below — collab counted that no-op as a genuine change, permanently
    // marking the topic as pending. Both sides are already delimiter-stripped, so a plain string
    // comparison detects the no-op case.
    const isNoOpSave = save && (!hasContentChange || (!!newLatex && newLatex === this.currentLatex));

    if (save && newLatex && !isNoOpSave) {
      const newNode = this.buildSavedNode(newLatex);
      // pos can fail to point at an equation anymore — e.g. collab delivered a remove/replace of
      // this node while it was being edited — in which case trySaveEquation() already restores
      // the display without touching the document.
      if (!this.trySaveEquation(pos, newNode, refocus)) {
        return;
      }
    } else if ((!save || isNoOpSave) && this.currentLatex) {
      // Escaped from an existing equation, or a "save" that turned out to be a no-op — either
      // way nothing changed, so just restore the display without touching the document.
      if (isNoOpSave && refocus) {
        // A real save gets this for free from saveEquation()'s own tr.setSelection(); a no-op
        // commit runs no transaction, so do it here instead. Skipped on click-away (refocus ===
        // false) — that click already placed the caret.
        this.collapseSelectionAfterEquation(pos);
      }
      this.restoreDisplayAfterUnsavedExit(refocus);
      return;
    } else if (save && this.currentLatex) {
      const emptyNode = this.node.type.create(this.node.attrs);
      if (!this.trySaveEquation(pos, emptyNode, refocus)) {
        return;
      }
    } else {
      this.restoreDisplayAfterUnsavedExit(refocus);
      return;
    }

    if (refocus) {
      this.editorView.focus();
    }
  }

  /**
   * Dispatches a saveEquation() transaction, restoring the display without touching the document
   * (the same stale-position recovery path used by every other unsaved exit) if pos no longer
   * points at an equation. Returns whether the save actually happened, so exitEditMode() knows
   * whether to keep going (e.g. refocus) or bail out.
   */
  private trySaveEquation(pos: number, node: ProseMirrorNode, refocus: boolean): boolean {
    if (saveEquation(pos, node)(this.editorView.state, tr => this.editorView.dispatch(tr))) {
      return true;
    }

    this.restoreDisplayAfterUnsavedExit(refocus);
    return false;
  }

  private restoreDisplayAfterUnsavedExit(refocus: boolean) {
    this.syncNode(this.node);

    if (refocus) {
      this.editorView.focus();
    }
  }

  /**
   * Collapses the selection to a caret just after the equation at `pos`, mirroring what
   * saveEquation()'s own transaction already does for a genuine content change (see
   * equation.command.ts) — needed as a separate step only for a no-op save (see exitEditMode's
   * isNoOpSave branch), since no transaction runs there to carry a selection change along with it.
   * Reads the node fresh and checks its type first so a node removed/replaced elsewhere in the
   * document while it was being edited isn't mis-selected.
   */
  private collapseSelectionAfterEquation(pos: number) {
    const currentNode = this.editorView.state.doc.nodeAt(pos);
    if (currentNode?.type === this.node.type) {
      this.editorView.dispatch(
        this.editorView.state.tr.setSelection(TextSelection.create(this.editorView.state.doc, pos + currentNode.nodeSize))
      );
    }
  }

  /**
   * Builds the node to save back into the document, preserving raw MathML content instead of
   * escaping it, the original node's attrs (e.g. tracked-change data added by
   * MadCapChangeSchemaPlugin) instead of resetting them to schema defaults, and the original text
   * child's marks (e.g. a madcapchange tracked-change mark) instead of dropping them. See
   * `buildSavedEquationNode`.
   */
  private buildSavedNode(newLatex: string): ProseMirrorNode {
    return this.node.type.create(
      this.node.attrs,
      buildSavedEquationNode(this.node.type.schema, newLatex, this.node.firstChild?.marks)
    );
  }

  /**
   * Wraps the equation in a horizontal strike for tracked-removal display.
   * MathML uses `<menclose>`; TeX uses `\enclose`.
   */
  private withHorizontalStrike(equation: string): string {
    if (!equation) {
      return equation;
    }

    if (equation.startsWith('<math') || equation.startsWith('<mml:')) {
      return this.withMathMlHorizontalStrike(equation);
    }

    return `\\enclose{horizontalstrike}{${equation}}`;
  }

  private withMathMlHorizontalStrike(equation: string): string {
    const match = equation.match(wrappedMathRegex);

    if (!match) {
      return equation;
    }

    const mathTagName = match[1];
    const mathAttrs = match[2] ?? '';
    const mathContent = match[3] ?? '';
    const mencloseTagName = mathTagName.startsWith('mml:') ? 'mml:menclose' : 'menclose';

    return `<${mathTagName}${mathAttrs}><${mencloseTagName} notation="horizontalstrike">${mathContent}</${mencloseTagName}></${mathTagName}>`;
  }
}
